import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import {
  Bot,
  CheckCircle2,
  KeyRound,
  Mic2,
  Plus,
  RefreshCw,
  Save,
  ShieldCheck,
  SlidersHorizontal,
  Trash2,
  Users,
} from 'lucide-react';
import { useAuth } from '@/core/hooks';

interface AssistantPersona {
  displayName: string;
  gender: 'female' | 'male' | 'neutral';
  voiceTone: string;
  voiceTimbre: string;
  speakingPace: string;
  formality: string;
  answerLength: string;
  voiceId: string;
}

interface AssistantAgent {
  id: string;
  name: string;
  provider: string;
  endpoint: string;
  model: string;
  apiKey: string;
  enabled: boolean;
  active: boolean;
  notes: string;
  apiKeyConfigured?: boolean;
  apiKeyPreview?: string;
}

interface AssistantScript {
  id: string;
  title: string;
  category: string;
  enabled: boolean;
  content: string;
}

interface AssistantConfig {
  persona: AssistantPersona;
  agents: AssistantAgent[];
  scripts: AssistantScript[];
  userAccess: Record<string, boolean>;
}

interface AssistantUserAccess {
  id: string;
  name: string;
  email: string;
  roles: string[];
  disabled: boolean;
  aiEnabled: boolean;
  locked?: boolean;
}

const defaultPersona: AssistantPersona = {
  displayName: 'Fuchsia Assistant',
  gender: 'female',
  voiceTone: 'calm, warm and professional',
  voiceTimbre: 'soft, clear and reassuring',
  speakingPace: 'normal',
  formality: 'professional and friendly',
  answerLength: 'concise',
  voiceId: 'alloy',
};

const emptyConfig: AssistantConfig = {
  persona: defaultPersona,
  agents: [],
  scripts: [],
  userAccess: {},
};

function authHeaders(token?: string | null): HeadersInit {
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function createAgent(): AssistantAgent {
  const id = `agent-${Date.now()}`;
  return {
    id,
    name: 'New AI Agent',
    provider: 'openai',
    endpoint: 'https://api.openai.com/v1/responses',
    model: 'gpt-5-mini',
    apiKey: '',
    enabled: true,
    active: false,
    notes: '',
  };
}

function createScript(): AssistantScript {
  return {
    id: `script-${Date.now()}`,
    title: 'New assistant rule',
    category: 'Custom rule',
    enabled: true,
    content: 'Describe exactly how the assistant should behave in this situation.',
  };
}

export const AssistantAIControl: React.FC = () => {
  const { token } = useAuth();
  const [config, setConfig] = useState<AssistantConfig>(emptyConfig);
  const [users, setUsers] = useState<AssistantUserAccess[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const activeAgent = useMemo(
    () => config.agents.find((agent) => agent.active && agent.enabled) || config.agents.find((agent) => agent.enabled),
    [config.agents]
  );

  const loadConfig = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/ai/assistant-control', {
        headers: authHeaders(token),
      });
      if (!response.ok) throw new Error(`Server returned ${response.status}`);
      const payload = await response.json() as { data?: AssistantConfig; users?: AssistantUserAccess[] };
      setConfig({
        ...emptyConfig,
        ...(payload.data || {}),
        persona: { ...defaultPersona, ...(payload.data?.persona || {}) },
        agents: Array.isArray(payload.data?.agents) ? payload.data.agents : [],
        scripts: Array.isArray(payload.data?.scripts) ? payload.data.scripts : [],
        userAccess: payload.data?.userAccess || {},
      });
      setUsers(Array.isArray(payload.users) ? payload.users : []);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not load Assistant AI control panel');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadConfig();
  }, [token]);

  const saveConfig = async () => {
    setSaving(true);
    try {
      const response = await fetch('/api/ai/assistant-control', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders(token),
        },
        body: JSON.stringify({ data: config }),
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => ({})) as { error?: string };
        throw new Error(payload.error || `Server returned ${response.status}`);
      }
      const payload = await response.json() as { data?: AssistantConfig; users?: AssistantUserAccess[] };
      if (payload.data) {
        setConfig({
          ...payload.data,
          persona: { ...defaultPersona, ...payload.data.persona },
          agents: payload.data.agents || [],
          scripts: payload.data.scripts || [],
          userAccess: payload.data.userAccess || {},
        });
      }
      if (Array.isArray(payload.users)) setUsers(payload.users);
      toast.success('Assistant AI control settings saved securely on the server.');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not save Assistant AI settings');
    } finally {
      setSaving(false);
    }
  };

  const updatePersona = (patch: Partial<AssistantPersona>) => {
    setConfig((current) => ({
      ...current,
      persona: { ...current.persona, ...patch },
    }));
  };

  const updateAgent = (agentId: string, patch: Partial<AssistantAgent>) => {
    setConfig((current) => ({
      ...current,
      agents: current.agents.map((agent) => {
        if (agent.id !== agentId) {
          return patch.active ? { ...agent, active: false } : agent;
        }
        return { ...agent, ...patch };
      }),
    }));
  };

  const removeAgent = (agentId: string) => {
    setConfig((current) => {
      const nextAgents = current.agents.filter((agent) => agent.id !== agentId);
      return {
        ...current,
        agents: nextAgents.some((agent) => agent.active) || nextAgents.length === 0
          ? nextAgents
          : nextAgents.map((agent, index) => ({ ...agent, active: index === 0 })),
      };
    });
  };

  const updateScript = (scriptId: string, patch: Partial<AssistantScript>) => {
    setConfig((current) => ({
      ...current,
      scripts: current.scripts.map((script) => script.id === scriptId ? { ...script, ...patch } : script),
    }));
  };

  const removeScript = (scriptId: string) => {
    setConfig((current) => ({
      ...current,
      scripts: current.scripts.filter((script) => script.id !== scriptId),
    }));
  };

  const toggleUserAccess = (userId: string, enabled: boolean) => {
    setConfig((current) => ({
      ...current,
      userAccess: {
        ...current.userAccess,
        [userId]: enabled,
      },
    }));
    setUsers((current) => current.map((user) => user.id === userId ? { ...user, aiEnabled: enabled } : user));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 p-6">
        <div className="rounded-md border border-slate-200 bg-white p-8 text-center text-slate-600">
          Loading Assistant AI control panel...
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <div className="flex items-center gap-3 text-sm font-semibold uppercase tracking-wide text-purple-700">
                <Bot className="h-5 w-5" />
                Owner-only assistant control
              </div>
              <h1 className="mt-2 text-3xl font-bold text-slate-950">Assistant AI Control Panel</h1>
              <p className="mt-2 max-w-3xl text-sm text-slate-600">
                Configure the assistant persona, voice style, active AI agent, training scripts and which application users can access Assistant AI.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button type="button" onClick={() => void loadConfig()} className="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100">
                <RefreshCw className="h-4 w-4" />
                Refresh
              </button>
              <button type="button" disabled={saving} onClick={() => void saveConfig()} className="inline-flex items-center gap-2 rounded-md bg-purple-700 px-4 py-2 text-sm font-semibold text-white hover:bg-purple-800 disabled:opacity-60">
                <Save className="h-4 w-4" />
                Save settings
              </button>
            </div>
          </div>

          <div className="mt-6 grid gap-3 md:grid-cols-4">
            <div className="rounded-md border border-slate-200 bg-white p-4">
              <div className="text-sm font-medium text-slate-600">Active agent</div>
              <div className="mt-2 text-lg font-bold text-slate-950">{activeAgent?.name || 'No active agent'}</div>
            </div>
            <div className="rounded-md border border-slate-200 bg-white p-4">
              <div className="text-sm font-medium text-slate-600">Voice persona</div>
              <div className="mt-2 text-lg font-bold capitalize text-slate-950">{config.persona.gender}</div>
            </div>
            <div className="rounded-md border border-slate-200 bg-white p-4">
              <div className="text-sm font-medium text-slate-600">Rules enabled</div>
              <div className="mt-2 text-lg font-bold text-slate-950">{config.scripts.filter((script) => script.enabled).length}</div>
            </div>
            <div className="rounded-md border border-slate-200 bg-white p-4">
              <div className="text-sm font-medium text-slate-600">Users with AI access</div>
              <div className="mt-2 text-lg font-bold text-slate-950">{users.filter((user) => user.aiEnabled && !user.disabled).length}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto grid max-w-7xl gap-5 px-4 py-6 sm:px-6 lg:px-8">
        <section className="rounded-md border border-slate-200 bg-white p-5">
          <div className="flex items-center gap-2">
            <Mic2 className="h-5 w-5 text-purple-700" />
            <h2 className="text-xl font-bold text-slate-950">Assistant persona and voice</h2>
          </div>
          <div className="mt-5 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <input value={config.persona.displayName} onChange={(event) => updatePersona({ displayName: event.target.value })} placeholder="Assistant name" className="rounded-md border border-slate-300 px-3 py-2 text-sm" />
            <select value={config.persona.gender} onChange={(event) => updatePersona({ gender: event.target.value as AssistantPersona['gender'] })} className="rounded-md border border-slate-300 px-3 py-2 text-sm">
              <option value="female">Female</option>
              <option value="male">Male</option>
              <option value="neutral">Neutral</option>
            </select>
            <input value={config.persona.voiceId} onChange={(event) => updatePersona({ voiceId: event.target.value })} placeholder="Voice ID" className="rounded-md border border-slate-300 px-3 py-2 text-sm" />
            <select value={config.persona.answerLength} onChange={(event) => updatePersona({ answerLength: event.target.value })} className="rounded-md border border-slate-300 px-3 py-2 text-sm">
              <option value="very concise">Very concise</option>
              <option value="concise">Concise</option>
              <option value="balanced">Balanced</option>
              <option value="detailed when needed">Detailed when needed</option>
            </select>
            <input value={config.persona.voiceTone} onChange={(event) => updatePersona({ voiceTone: event.target.value })} placeholder="Voice tone" className="rounded-md border border-slate-300 px-3 py-2 text-sm lg:col-span-2" />
            <input value={config.persona.voiceTimbre} onChange={(event) => updatePersona({ voiceTimbre: event.target.value })} placeholder="Voice timbre" className="rounded-md border border-slate-300 px-3 py-2 text-sm lg:col-span-2" />
            <input value={config.persona.speakingPace} onChange={(event) => updatePersona({ speakingPace: event.target.value })} placeholder="Speaking pace" className="rounded-md border border-slate-300 px-3 py-2 text-sm lg:col-span-2" />
            <input value={config.persona.formality} onChange={(event) => updatePersona({ formality: event.target.value })} placeholder="Formality" className="rounded-md border border-slate-300 px-3 py-2 text-sm lg:col-span-2" />
          </div>
        </section>

        <section className="rounded-md border border-slate-200 bg-white p-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <KeyRound className="h-5 w-5 text-purple-700" />
              <h2 className="text-xl font-bold text-slate-950">AI agents and API keys</h2>
            </div>
            <button type="button" onClick={() => setConfig((current) => ({ ...current, agents: [...current.agents, createAgent()] }))} className="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100">
              <Plus className="h-4 w-4" />
              Add agent
            </button>
          </div>
          <div className="mt-4 grid gap-4">
            {config.agents.map((agent) => (
              <div key={agent.id} className="rounded-md border border-slate-200 bg-slate-50 p-4">
                <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
                  <input value={agent.name} onChange={(event) => updateAgent(agent.id, { name: event.target.value })} className="rounded-md border border-slate-300 px-3 py-2 text-sm" placeholder="Agent name" />
                  <input value={agent.provider} onChange={(event) => updateAgent(agent.id, { provider: event.target.value })} className="rounded-md border border-slate-300 px-3 py-2 text-sm" placeholder="Provider" />
                  <input value={agent.model} onChange={(event) => updateAgent(agent.id, { model: event.target.value })} className="rounded-md border border-slate-300 px-3 py-2 text-sm" placeholder="Model" />
                  <input value={agent.endpoint} onChange={(event) => updateAgent(agent.id, { endpoint: event.target.value })} className="rounded-md border border-slate-300 px-3 py-2 text-sm" placeholder="Endpoint URL" />
                  <input
                    value={agent.apiKey}
                    onChange={(event) => updateAgent(agent.id, { apiKey: event.target.value })}
                    className="rounded-md border border-slate-300 px-3 py-2 text-sm lg:col-span-2"
                    placeholder={agent.apiKeyConfigured ? `API key already ${agent.apiKeyPreview || 'configured'} - enter a new key to replace` : 'API key'}
                    type="password"
                  />
                  <input value={agent.notes} onChange={(event) => updateAgent(agent.id, { notes: event.target.value })} className="rounded-md border border-slate-300 px-3 py-2 text-sm lg:col-span-2" placeholder="Notes" />
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <button type="button" onClick={() => updateAgent(agent.id, { enabled: !agent.enabled })} className={`rounded-md px-3 py-2 text-xs font-semibold ${agent.enabled ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-200 text-slate-700'}`}>
                    {agent.enabled ? 'On' : 'Off'}
                  </button>
                  <button type="button" onClick={() => updateAgent(agent.id, { active: true, enabled: true })} className={`inline-flex items-center gap-2 rounded-md px-3 py-2 text-xs font-semibold ${agent.active ? 'bg-purple-700 text-white' : 'border border-slate-300 bg-white text-slate-700'}`}>
                    <CheckCircle2 className="h-4 w-4" />
                    {agent.active ? 'Active agent' : 'Make active'}
                  </button>
                  <button type="button" disabled={config.agents.length <= 1} onClick={() => removeAgent(agent.id)} className="inline-flex items-center gap-2 rounded-md border border-red-200 bg-white px-3 py-2 text-xs font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50">
                    <Trash2 className="h-4 w-4" />
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="rounded-md border border-slate-200 bg-white p-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <SlidersHorizontal className="h-5 w-5 text-purple-700" />
              <h2 className="text-xl font-bold text-slate-950">Assistant scripts and rules</h2>
            </div>
            <button type="button" onClick={() => setConfig((current) => ({ ...current, scripts: [...current.scripts, createScript()] }))} className="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100">
              <Plus className="h-4 w-4" />
              Add script
            </button>
          </div>
          <div className="mt-4 grid gap-4 lg:grid-cols-2">
            {config.scripts.map((script) => (
              <div key={script.id} className="rounded-md border border-slate-200 bg-slate-50 p-4">
                <div className="grid gap-3 md:grid-cols-2">
                  <input value={script.title} onChange={(event) => updateScript(script.id, { title: event.target.value })} className="rounded-md border border-slate-300 px-3 py-2 text-sm" placeholder="Rule title" />
                  <input value={script.category} onChange={(event) => updateScript(script.id, { category: event.target.value })} className="rounded-md border border-slate-300 px-3 py-2 text-sm" placeholder="Category" />
                </div>
                <textarea value={script.content} onChange={(event) => updateScript(script.id, { content: event.target.value })} rows={6} className="mt-3 w-full rounded-md border border-slate-300 px-3 py-2 text-sm" placeholder="Training script, rule or behaviour instruction" />
                <div className="mt-3 flex flex-wrap gap-2">
                  <button type="button" onClick={() => updateScript(script.id, { enabled: !script.enabled })} className={`rounded-md px-3 py-2 text-xs font-semibold ${script.enabled ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-200 text-slate-700'}`}>
                    {script.enabled ? 'Rule on' : 'Rule off'}
                  </button>
                  <button type="button" onClick={() => removeScript(script.id)} className="inline-flex items-center gap-2 rounded-md border border-red-200 bg-white px-3 py-2 text-xs font-semibold text-red-700 hover:bg-red-50">
                    <Trash2 className="h-4 w-4" />
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="rounded-md border border-slate-200 bg-white p-5">
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-purple-700" />
            <h2 className="text-xl font-bold text-slate-950">User access to Assistant AI</h2>
          </div>
          <p className="mt-2 text-sm text-slate-600">Turn Assistant AI access on or off for each registered user. The owner account remains enabled.</p>
          <div className="mt-4 overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead>
                <tr className="text-left text-slate-600">
                  <th className="py-2 pr-3">User</th>
                  <th className="py-2 pr-3">Roles</th>
                  <th className="py-2 pr-3">Status</th>
                  <th className="py-2 pr-3">Assistant AI</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {users.map((appUser) => (
                  <tr key={appUser.id}>
                    <td className="py-3 pr-3">
                      <div className="font-semibold text-slate-950">{appUser.name}</div>
                      <div className="text-xs text-slate-500">{appUser.email}</div>
                    </td>
                    <td className="py-3 pr-3 text-slate-600">{appUser.roles.join(', ')}</td>
                    <td className="py-3 pr-3">
                      <span className={`rounded-full px-2 py-1 text-xs font-semibold ${appUser.disabled ? 'bg-red-100 text-red-800' : 'bg-emerald-100 text-emerald-800'}`}>
                        {appUser.disabled ? 'Disabled' : 'Active'}
                      </span>
                    </td>
                    <td className="py-3 pr-3">
                      <button
                        type="button"
                        disabled={appUser.locked}
                        onClick={() => toggleUserAccess(appUser.id, !appUser.aiEnabled)}
                        className={`inline-flex items-center gap-2 rounded-md px-3 py-2 text-xs font-semibold disabled:cursor-not-allowed disabled:opacity-70 ${
                          appUser.aiEnabled ? 'bg-purple-700 text-white' : 'border border-slate-300 bg-white text-slate-700'
                        }`}
                      >
                        <ShieldCheck className="h-4 w-4" />
                        {appUser.aiEnabled ? 'On' : 'Off'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      {saving ? (
        <div className="fixed bottom-4 right-4 rounded-md bg-slate-950 px-4 py-3 text-sm font-semibold text-white shadow-lg">
          Saving Assistant AI settings...
        </div>
      ) : null}
    </div>
  );
};
